// Phone composer height — the user drags a handle to set the input area's
// height between COMPOSER_MIN_PX and COMPOSER_MAX_RATIO of the chat panel. The
// chosen height persists to localStorage; the height SHOWN is that value capped
// to the panel's current size, so a height saved on a taller screen (or before
// rotating to landscape) can't crowd out the message log.
import { useEffect, useRef, useState } from 'react';
import type { TouchEvent as ReactTouchEvent } from 'react';
import { COMPOSER_MAX_RATIO, COMPOSER_MIN_PX } from '@/lib/uiConstants';

export const COMPOSER_HEIGHT_KEY = 'mobile-composer-h';

function readSavedHeight(): number {
  try {
    const saved = parseInt(localStorage.getItem(COMPOSER_HEIGHT_KEY) ?? '', 10);
    return Number.isFinite(saved) ? Math.max(COMPOSER_MIN_PX, saved) : COMPOSER_MIN_PX;
  } catch {
    return COMPOSER_MIN_PX;
  }
}

/** `enabled` is false on desktop, where the composer is a resizable panel instead. */
export function usePhoneComposerHeight(enabled: boolean) {
  const [chosenHeight, setChosenHeight] = useState(readSavedHeight);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setContainerHeight(el.clientHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  const maxHeight =
    containerHeight > 0 ? Math.max(COMPOSER_MIN_PX, Math.floor(containerHeight * COMPOSER_MAX_RATIO)) : Infinity;
  const height = Math.min(chosenHeight, maxHeight);

  const onTouchStart = (e: ReactTouchEvent): void => {
    const startY = e.touches[0]?.clientY ?? 0;
    const startHeight = height;
    // The drag's latest height, saved on release — the state value captured
    // by this handler is the height from before the drag.
    let latest = startHeight;

    const onMove = (ev: TouchEvent): void => {
      // Dragging up (pointer Y decreasing) grows the composer.
      const delta = startY - (ev.touches[0]?.clientY ?? startY);
      latest = Math.max(COMPOSER_MIN_PX, Math.min(maxHeight, startHeight + delta));
      setChosenHeight(latest);
    };
    const onEnd = (): void => {
      try {
        localStorage.setItem(COMPOSER_HEIGHT_KEY, String(latest));
      } catch {
        /* quota or private mode — the height just isn't remembered */
      }
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };

    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd);
  };

  return { containerRef, height, minHeight: COMPOSER_MIN_PX, onTouchStart };
}
